import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Link from 'next/link';
import { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Premium from '../public/icons/go-premium/premium.svg';
import { usePlanQuery } from '../redux/services/payments/payments.generated';
import { closeBlocker, isBlockerOpenSelector } from '../redux/slices/authSlice';
import { useUserCurrentQuery } from '../redux/services/auth/auth.generated';
import PremiumIcon2x from '/public/icons/common/premium-plan-2x.svg';
import Cookies from 'js-cookie';

const PremiumBlocker: FunctionComponent = () => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const userToken = Cookies.get('ragya-token');
  const { data: userData } = useUserCurrentQuery({}, { skip: !userToken });

  const open = useSelector(isBlockerOpenSelector);

  const { data: planData, isFetching } = usePlanQuery();

  const dispatch = useDispatch();

  return (
    <Drawer
      anchor={'right'}
      open={open}
      onClose={() => dispatch(closeBlocker())}
      sx={{
        zIndex: 5000,
        '& .MuiPaper-root': {
          backgroundColor: 'white',
          maxWidth: 440,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <PremiumIcon2x />
            <Typography
              sx={{
                color: (theme) => theme.ragyaPalette.gray10,
                fontSize: 24,
                fontWeight: 700,
                marginLeft: 1,
              }}
            >
              Subscribe & Get
            </Typography>
          </Box>
          <Button
            sx={{
              backgroundColor: '#E9F3F3',
              borderRadius: '17px',
              height: 30,
              m: 2,
            }}
            onClick={() => dispatch(closeBlocker())}
          >
            <Typography
              sx={{
                color: (theme) => theme.ragyaPalette.gray12,
                textTransform: 'none',
              }}
              variant='body1'
            >
              Later
            </Typography>
          </Button>
        </Box>

        {/* New: Already a user? Sign in */}
        {!userData?.userCurrent && (
          <Box sx={{ pl: '10px', mb: '8px' }}>
            <Typography
              variant='body1'
              sx={{
                color: (theme) => theme.ragyaPalette.gray5,
                fontWeight: 500,
              }}
            >
              Already a user?{' '}
              <Link href='/login' passHref>
                <Typography
                  component='span'
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => dispatch(closeBlocker())}
                >
                  Sign in
                </Typography>
              </Link>
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            p: 1,
          }}
        >
          <Typography
            variant='body1'
            sx={{
              color: (theme) => theme.ragyaPalette.gray12,
              border: '2px solid #CEE3E3',
              backgroundColor: '#E9F3F3',
              borderRadius: '8px',
              marginBottom: 2,
              lineHeight: 1.7,
            }}
            component={'div'}
          >
            <ul>
              <li>
                Access to <b>1000+ hours</b> of artiste-curated music
              </li>
              <li>
                <b>
                  <i>Browse</i>
                </b>{' '}
                & play by artiste, raga, and instrument
              </li>
              <li>
                Set Vocal & Instrumental preferences in{' '}
                <b>
                  <i>Prahar</i>
                </b>
              </li>
              <li>
                Create and play your own <b>{`'My Favourites'`}</b> playlist
              </li>
              <li>
                Enjoy better audio quality of <b>192+ kbps</b>
              </li>
            </ul>
          </Typography>

          <Typography
            variant={'h2'}
            sx={{
              color: 'rgba(58, 73, 73, 1)',
              textAlign: 'left',
              fontSize: 12,
              letterSpacing: '0.255em',
              pt: 2,
            }}
          >
            SELECT YOUR PLAN
          </Typography>

          <Box>
            {isFetching ? (
              <CircularProgress size={80} />
            ) : (
              planData?.plans.map((plan) => (
                <Box key={plan.id}>
                  <Divider
                    sx={{
                      width: '100%',
                      my: 2,
                      border: '1px solid #D3E4E4',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      my: 1,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: (theme) => theme.ragyaPalette.gray12,
                        }}
                        variant='h2'
                      >
                        {plan.name}
                      </Typography>
                      <Typography
                        variant={'subtitle1'}
                        align={'left'}
                        sx={{ color: (theme) => theme.ragyaPalette.gray12 }}
                      >
                        {plan.description}
                      </Typography>
                    </Box>
                    <Box display={'flex'} sx={{ alignItems: 'center' }}>
                      <Typography
                        variant={'button'}
                        sx={{
                          color: (theme) => theme.ragyaPalette.ragyaOrange,
                          p: 0.4,
                        }}
                        align={'right'}
                      >
                        {plan.currencySymbol}
                      </Typography>
                      <Typography
                        variant={'button'}
                        sx={{
                          color: (theme) => theme.ragyaPalette.ragyaOrange,
                          paddingRight: 1.2,
                        }}
                        align={'right'}
                      >
                        {plan.price}
                      </Typography>
                      <Link
                        passHref
                        href={
                          userData?.userCurrent.id
                            ? `/pricing/${plan.id}`
                            : '/login'
                        }
                      >
                        <Button
                          variant='contained'
                          color='primary'
                          sx={{ my: 0.5, mx: 'auto' }}
                          onClick={() => dispatch(closeBlocker())}
                        >
                          SELECT
                        </Button>
                      </Link>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
            <Divider
              sx={{
                width: '100%',
                my: 2,
                border: '1px solid #D3E4E4',
              }}
            />

            <Typography
              variant='body1'
              sx={{
                color: (theme) => theme.ragyaPalette.gray5,
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline', // Add this line for underline on hover
                },
              }}
              onClick={() => {
                dispatch(closeBlocker());
                window.open('/go-premium', '_blank', 'noopener,noreferrer');
              }}
            >
              Learn more about Premium
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PremiumBlocker;
